import { window } from '../env/browser';
import type { ErrorLogger } from '../shared/types';

interface SentryScope {
  setTag: (key: string, value: unknown) => void;
  setExtra: (key: string, value: unknown) => void;
  setUser: (user: { email?: string; id?: string }) => void;
}

// Shape of the Sentry browser SDK exposed on window by the Shore CDN —
// only the parts this module touches.
export interface SentryClient {
  // All methods optional because window.Sentry may be only partially
  // loaded — the logger falls back to the console when they're absent.
  captureException?: (error: Error) => void;
  withScope?: (callback: (scope: SentryScope) => void) => void;
  configureScope?: (callback: (scope: SentryScope) => void) => void;
}

function logErrorToConsole(
  error: Error,
  context?: Record<string, unknown>,
  extraTagsFromMicroApp: Record<string, unknown> = {}
) {
  if (window.console) {
    window.console.error(error, context, extraTagsFromMicroApp);
  }
}

function logErrorToApi(
  Sentry: SentryClient,
  error: Error,
  context: Record<string, unknown> = {},
  extraTagsFromMicroApp: Record<string, unknown> = {}
) {
  const extra = context.extra || {
    cause: error.cause && String(error.cause),
  };

  Sentry.withScope?.((scope) => {
    scope.setTag('extra', extra);

    Object.keys(extraTagsFromMicroApp).forEach((key) => {
      scope.setTag(key, extraTagsFromMicroApp[key]);
    });

    Object.keys(context).forEach((key) => {
      if (key !== 'extra') {
        scope.setExtra(key, context[key]);
      }
    });

    Sentry.captureException?.(error);
  });
}

export default function createErrorLogger(Sentry?: SentryClient): ErrorLogger {
  return {
    log: (error, context, extraTagsFromMicroApp) => {
      if (Sentry?.captureException && Sentry.withScope) {
        logErrorToApi(Sentry, error, context, extraTagsFromMicroApp);
      } else {
        logErrorToConsole(error, context, extraTagsFromMicroApp);
      }
    },
    setContext: ({ email, id }) => {
      if (Sentry?.configureScope) {
        Sentry.configureScope((scope) => {
          scope.setUser({
            email,
            id,
          });
        });
      }
    },
  };
}
